import React from 'react';

/**
 * Shared MINT source picker. ALWAYS shown at the top of every settlement form
 * across all providers (TKM, SOVEIN, ALT1, Mandiri, FIN.COM, SWIFT) to enforce
 * provenance — no settlement can be initiated without a MINT source.
 *
 * Props:
 *   accounts        — array of {id, key, name, number, balance} from /api/usdmint/accounts
 *   selectedId      — currently selected MINT account id
 *   onSelect(id)    — callback when user picks an account
 *   providerLabel   — short label used in copy (e.g. "TKM", "SOVEIN")
 *   onRefresh       — optional refresh callback (when accounts list is empty)
 *   amount          — optional current amount in the form, used to render
 *                     "Balance after this debit" preview
 *   compact         — compact mode for forms in narrow tabs
 */
const MintSourcePicker = ({
  accounts = [],
  selectedId = '',
  onSelect,
  providerLabel = 'Settlement',
  onRefresh,
  amount,
  compact = false,
  testIdPrefix = 'mint-source',
}) => {
  const amt = parseFloat(amount) || 0;
  return (
    <div className="border border-sky-200 bg-sky-50/40 rounded-lg p-3 mb-4" data-testid={`${testIdPrefix}-picker-wrap`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wide text-sky-800 font-semibold">
          Step 1 — Pick MINT source account for {providerLabel}
        </p>
        {!selectedId && (
          <span className="text-[10px] text-rose-700">⚠ required</span>
        )}
      </div>
      <div className={`grid ${compact ? 'grid-cols-3 gap-2' : 'grid-cols-1 md:grid-cols-3 gap-2'}`} data-testid={`${testIdPrefix}-cards`}>
        {accounts.length === 0 && (
          <button onClick={onRefresh} className="col-span-3 text-xs underline text-sky-700">
            Load MINT accounts
          </button>
        )}
        {accounts.map((a) => {
          const balance = Number(a.balance || 0);
          const remaining = balance - amt;
          const overdrawn = amt > 0 && remaining < 0;
          const isSelected = selectedId === a.id;
          return (
            <button
              key={a.id}
              type="button"
              data-testid={`${testIdPrefix}-${a.key}`}
              onClick={() => onSelect && onSelect(a.id)}
              className={`text-left rounded-md p-2 border transition ${
                isSelected
                  ? 'border-sky-500 bg-white shadow-sm'
                  : 'border-slate-200 bg-white hover:bg-sky-50'
              }`}
            >
              <p className="text-[10px] uppercase tracking-wide text-slate-500">{a.key}</p>
              <p className="text-xs font-semibold text-slate-800 truncate">{a.name}</p>
              <p className="text-[10px] font-mono text-slate-500">{a.number}</p>
              <p className="font-mono text-sm text-slate-900 mt-0.5">
                ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              {isSelected && amt > 0 && (
                <p className={`text-[10px] font-mono mt-0.5 ${overdrawn ? 'text-rose-700' : 'text-emerald-700'}`}>
                  → after debit: $
                  {remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {overdrawn && ' (overdrawn!)'}
                </p>
              )}
            </button>
          );
        })}
      </div>
      {!selectedId && (
        <p className="text-[11px] text-rose-700 mt-2" data-testid={`${testIdPrefix}-required-msg`}>
          ⚠ Cannot proceed — settlements require a chosen MINT source for provenance.
        </p>
      )}
    </div>
  );
};

export default MintSourcePicker;
